/**
 * Investor One-Pager PDF download modal.
 *
 * Gated download — recipient must provide ALL of: full name, company,
 * email, mobile. On submit:
 *   1. POST to /api/investors/request-pdf with the 4 fields
 *   2. Receive the PDF as a blob
 *   3. Trigger a browser download with the filename from Content-Disposition
 *   4. Show a brief success state, then auto-close
 *
 * The backend captures the lead into `investor_pdf_requests` and fires the
 * standard Postmark + Twilio fan-out so Robert sees the prospect in real-time.
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, Download, Loader2, CheckCircle2 } from 'lucide-react';

const PALETTE = {
  coral: '#0A2540',
  textMain: '#0B1528',
  textMuted: '#475569',
  cardBorder: 'rgba(92,82,106,0.16)',
  bgBase: '#F8F9FA',
};

export const InvestorPdfModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', company: '', email: '', mobile: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open && firstFieldRef.current) firstFieldRef.current.focus();
    if (!open) {
      // Reset on close so the next open is clean
      setTimeout(() => {
        setForm({ name: '', company: '', email: '', mobile: '' });
        setSubmitting(false);
        setSuccess(false);
        setError('');
      }, 200);
    }
  }, [open]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = {
      name: form.name.trim(),
      company: form.company.trim(),
      email: form.email.trim(),
      mobile: form.mobile.trim(),
    };
    if (!trimmed.name || !trimmed.company || !trimmed.email || !trimmed.mobile) {
      setError('All four fields are required.');
      return;
    }
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backendUrl}/api/investors/request-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trimmed),
      });
      if (!res.ok) {
        let detail = 'Could not generate the PDF. Please try again.';
        try {
          const j = await res.json();
          detail = j?.detail || detail;
          if (Array.isArray(detail)) detail = detail[0]?.msg || 'Invalid input.';
        } catch {
          /* response wasn't JSON — keep the default detail */
        }
        throw new Error(detail);
      }
      // Extract filename from Content-Disposition; fall back to a sensible default
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = (match && match[1]) || 'HiBFF-Investor-One-Pager.pdf';

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setSuccess(true);
      setTimeout(() => onClose && onClose(), 2200);
    } catch (err) {
      setError(err.message || 'Unexpected error.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="investor-pdf-modal-title"
      data-testid="investor-pdf-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        background: 'rgba(31, 26, 40, 0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        animation: 'hibff-modal-fade 0.2s ease-out',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes hibff-modal-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes hibff-modal-rise { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: PALETTE.bgBase,
          borderRadius: 20,
          maxWidth: 520, width: '100%',
          maxHeight: '90vh', overflow: 'auto',
          boxShadow: '0 24px 48px rgba(0,0,0,0.32), 0 12px 24px rgba(0,0,0,0.18)',
          animation: 'hibff-modal-rise 0.25s ease-out',
        }}
      >
        {/* Header band */}
        <div style={{
          background: 'linear-gradient(135deg, #1F1A28 0%, #0B1528 60%, #3A2B2B 100%)',
          padding: '20px 24px',
          color: 'white',
          position: 'relative',
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
        }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="investor-pdf-modal-close"
            aria-label="Close download modal"
            style={{
              position: 'absolute', top: 14, right: 14,
              background: 'rgba(255,255,255,0.10)',
              border: 'none', color: 'white', cursor: 'pointer',
              width: 30, height: 30, borderRadius: 999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Download size={14} style={{ color: '#FCC9B4' }} />
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#FCC9B4' }}>
              Investor One-Pager
            </span>
          </div>
          <h2 id="investor-pdf-modal-title" style={{ margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            Download the branded snapshot
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 }}>
            Live cap-table figures, two-step capital path, and the revenue-scale table — packaged as a forwardable PDF. We capture the four fields below so we can follow up if useful.
          </p>
        </div>

        {/* Form / success state */}
        {success ? (
          <div style={{ padding: '32px 24px', textAlign: 'center' }} data-testid="investor-pdf-modal-success">
            <div style={{
              width: 56, height: 56, borderRadius: 999,
              background: '#E6F5E1', color: '#1F5C44',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 14,
            }}>
              <CheckCircle2 size={28} strokeWidth={2.5} />
            </div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: PALETTE.textMain }}>
              Your PDF is downloading
            </h3>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: PALETTE.textMuted }}>
              We&rsquo;ve also emailed a copy to your inbox. Robert &amp; Rebecca have been notified — expect a personal note shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{ padding: '22px 24px' }} data-testid="investor-pdf-modal-form">
            <div style={{ display: 'grid', gap: 14 }}>
              <Field label="Full name *" testid="pdf-field-name">
                <input
                  ref={firstFieldRef}
                  type="text" required minLength={2} maxLength={120}
                  value={form.name}
                  onChange={onChange('name')}
                  placeholder="Jane Doe"
                  data-testid="pdf-input-name"
                  style={inputStyle}
                />
              </Field>
              <Field label="Company / Fund *" testid="pdf-field-company">
                <input
                  type="text" required minLength={2} maxLength={160}
                  value={form.company}
                  onChange={onChange('company')}
                  placeholder="Brave Ventures"
                  data-testid="pdf-input-company"
                  style={inputStyle}
                />
              </Field>
              <Field label="Work email *" testid="pdf-field-email">
                <input
                  type="email" required maxLength={200}
                  value={form.email}
                  onChange={onChange('email')}
                  placeholder="jane@firm.vc"
                  data-testid="pdf-input-email"
                  style={inputStyle}
                />
              </Field>
              <Field label="Mobile *" testid="pdf-field-mobile">
                <input
                  type="tel" required minLength={7} maxLength={30}
                  value={form.mobile}
                  onChange={onChange('mobile')}
                  placeholder="+1 555 123 4567"
                  data-testid="pdf-input-mobile"
                  style={inputStyle}
                />
              </Field>

              {error && (
                <div
                  data-testid="investor-pdf-modal-error"
                  style={{
                    background: '#FFE3E3', color: '#8C0000',
                    border: '1px solid #FFC1C1', borderRadius: 10,
                    padding: '10px 12px', fontSize: 12, fontWeight: 600,
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 4 }}>
                <p style={{ margin: 0, fontSize: 11, color: PALETTE.textMuted, flex: 1 }}>
                  By downloading you consent to be contacted. We never share your details. Not a securities offer.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="investor-pdf-modal-submit"
                  style={{
                    background: PALETTE.coral, color: 'white',
                    border: 'none', borderRadius: 999,
                    padding: '11px 20px', fontSize: 13, fontWeight: 800,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    boxShadow: '0 6px 16px rgba(10, 37, 64, 0.40)',
                    opacity: submitting ? 0.7 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {submitting
                    ? (<><Loader2 size={14} className="animate-spin" /> Generating…</>)
                    : (<><Download size={14} /> Download PDF</>)}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 14,
  fontFamily: 'inherit',
  background: 'white',
  border: '1px solid rgba(92,82,106,0.18)',
  borderRadius: 10,
  outline: 'none',
  color: '#0B1528',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const Field = ({ label, testid, children }) => (
  <div data-testid={testid}>
    <label style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#475569', marginBottom: 6 }}>
      {label}
    </label>
    {children}
  </div>
);

export default InvestorPdfModal;
